import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  AssignProject,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import { Role, User } from 'library/types';
import apiService from 'library/api-service';
import { renderDataGridLocaleText } from 'utils';
import { useEffect, useMemo, useState } from 'react';
import { Button, Grid2 as Grid } from '@mui/material';
import SwipeUpIcon from '@mui/icons-material/SwipeUp';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLookup, useSingleRowSelectionGuard } from 'hooks';
import { CreateUserDialog, UpdateUserDialog } from './components';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';

const Users = () => {
  // other hooks
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const requireSingleRowSelection = useSingleRowSelectionGuard();
  const { items: roles, loading: loadingRoles } = useLookup<Role>(apiService.getRoles);

  // useState hooks
  const [data, setData] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDeleteUser, setOpenDeleteUser] = useState(false);
  const [openCreateUser, setOpenCreateUser] = useState(false);
  const [openUpdateUser, setOpenUpdateUser] = useState(false);
  const [openAssignProject, setOpenAssignProject] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'nameSurname',
        headerName: intl.formatMessage({ id: 'users.nameSurname', defaultMessage: 'Adı / Soyadı' }),
        flex: 1,
      },
      {
        field: 'email',
        headerName: intl.formatMessage({ id: 'users.email', defaultMessage: 'E-Posta' }),
        flex: 1,
      },
      {
        field: 'otherEmail',
        headerName: intl.formatMessage({ id: 'users.otherEmail', defaultMessage: 'E-Posta ( Diğer )' }),
        flex: 1,
      },
      {
        field: 'roleId',
        headerName: intl.formatMessage({ id: 'users.role', defaultMessage: 'Rol' }),
        flex: 0.5,
        valueGetter: (value: number) => roles.find((role) => role.id === value)?.name || '',
      },
      {
        field: 'isActivityUser',
        headerName: intl.formatMessage({
          id: 'users.isActivityUser',
          defaultMessage: 'Aktivite Kullanıcısı',
        }),
        flex: 0.5,
        type: 'boolean',
      },
      {
        field: 'status',
        headerName: intl.formatMessage({
          id: 'users.status',
          defaultMessage: 'Durum',
        }),
        flex: 0.5,
        type: 'boolean',
        valueGetter: (value: number) => Boolean(value),
      },
    ],
    [intl, roles]
  );

  // async helper functions
  const getValues = async () => {
    try {
      setLoading(true);

      const response = await apiService.getUsers();

      if (response.isSuccess) {
        setData(response.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const handleCloseAssignProject = () => setOpenAssignProject(false);
  const handleClickOpenAssignProject = async () => {
    const selectedUserId = requireSingleRowSelection(rowSelectionModel);
    if (selectedUserId === null) return;

    // all-show-books control
    const selectedUser = data.find((user) => user.id === selectedUserId) as User;

    if (selectedUser.roleId) {
      const {
        data: { permissionCodes },
      } = await apiService.getSpecificRole(selectedUser.roleId);

      if (permissionCodes.includes(STORAGE_KEY_ENUM.ALL_SHOW_BOOKS)) {
        enqueueSnackbar(
          <FormattedMessage
            id="users.allShowBooksControl"
            defaultMessage="Seçili kullanıcı sahip olduğu rol ile Tüm Projeler'e yetkilidir!"
          />,
          {
            variant: 'info',
          }
        );
        return;
      }
    }

    // If the check has been passed successfully...
    setOpenAssignProject(true);
  };

  const handleCloseDeleteUser = () => setOpenDeleteUser(false);
  const handleClickOpenDeleteUser = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;
    setOpenDeleteUser(true);
  };

  const handleCloseCreateUser = () => setOpenCreateUser(false);
  const handleClickOpenCreateUser = () => setOpenCreateUser(true);

  const handleCloseUpdateUser = () => setOpenUpdateUser(false);
  const handleClickOpenUpdateUser = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;
    setOpenUpdateUser(true);
  };

  // useEffect hooks
  useEffect(() => {
    getValues();
  }, []);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
        <PermissionHandler requiredPermissionCodes={['assign-project']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <Button
              onClick={handleClickOpenAssignProject}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<SwipeUpIcon />}
              color="primary">
              <FormattedMessage id="users.assignProject" defaultMessage="Proje Ata" />
            </Button>
          </Grid>
          <Grid size={{ lg: 4 }} sx={{ display: { xs: 'none', lg: 'block' } }} />
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['create-user']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateUser}>
              <FormattedMessage id="users.createUser" defaultMessage="Kullanıcı Oluştur" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-user']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateUser}>
              <FormattedMessage id="users.updateUser" defaultMessage="Kullanıcı Düzenle" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-user']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteUser}>
              <FormattedMessage id="users.deleteUser" defaultMessage="Kullanıcı Sil" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>
      <AssignProject
        open={openAssignProject}
        handleClose={handleCloseAssignProject}
        userId={rowSelectionModel[0]}
        refresh={getValues}
      />
      <CreateUserDialog open={openCreateUser} handleClose={handleCloseCreateUser} roles={roles} refresh={getValues} />
      <UpdateUserDialog
        open={openUpdateUser}
        handleClose={handleCloseUpdateUser}
        userId={rowSelectionModel[0]}
        roles={roles}
        refresh={getValues}
      />
      <DeleteDialog
        open={openDeleteUser}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteUser}
        refresh={getValues}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="users.deleteUser" defaultMessage="Kullanıcı Sil" />}
        subTitle={
          <FormattedMessage
            id="users.deleteUserRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra kullanıcı veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteUser}
        successMessage={
          <FormattedMessage
            id="users.deleteUserCompleted"
            defaultMessage="Seçili kullanıcıyı silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading || loadingRoles}
          localeText={renderDataGridLocaleText(intl)}
          rows={data}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default Users;
